import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import { getNewsById, getRelatedNews } from '@/utils/media';

export default function NewsDetailPage() {
  const { id = '' } = useParams();
  const item = getNewsById(id);

  useSeo({
    title: item ? `${item.title} - 黄色影视` : '影视资讯 - 黄色影视',
    description: item ? item.summary : '黄色影视资讯详情页。',
    keywords: item ? `${item.tags.join(',')},黄色影视,影视资讯` : '影视资讯,黄色影视',
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-2xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该资讯</h1>
          <p className="mt-3 text-sm text-foreground-600">这篇文章可能已被删除或链接有误。</p>
          <Link
            to="/news"
            className="mt-6 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedNews(item.id, 3);

  return (
    <SiteLayout>
      <article className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-500">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="cursor-pointer transition-colors hover:text-primary-500">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="line-clamp-1 text-foreground-800">{item.title}</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <header>
              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="rounded-full bg-accent-100 px-2.5 py-1 text-accent-900">{item.category}</span>
                <span>{item.date}</span>
                <span>·</span>
                <span>{item.author}</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
                {item.title}
              </h1>
            </header>

            <div className="mt-6 aspect-[16/7] w-full overflow-hidden rounded-lg bg-background-100">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 黄色影视资讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-4">
              {item.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-[1.9] text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {item.tags.map((tag) => (
                <span key={tag} className="rounded-md bg-secondary-100 px-3 py-1 text-sm text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 border-t border-background-200/70 pt-6">
              <Link
                to="/news"
                className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯列表
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-links-line text-primary-500"></i>
                相关资讯
              </h2>
              <ul className="mt-4 space-y-4">
                {related.map((news) => (
                  <li key={news.id}>
                    <Link to={`/news/${news.id}`} className="group flex cursor-pointer gap-3">
                      <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={news.cover}
                          alt={news.title}
                          title={`${news.title} - 黄色影视资讯`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-500">
                          {news.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">{news.date}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}